/**
 * Hook for fetching ordering status from the backend.
 * Provides time-aware status for lunch, breakfast, and kitchen views.
 */

import { useQuery } from '@tanstack/react-query';
import { apiConfig } from '../config';

// Types for ordering status
export interface LunchStatus {
  is_open: boolean;
  order_date: string | null;
  target_date: string | null;
  cutoff_time: string;
  reopen_time: string;
  current_time: string;
  message: string;
}

export interface BreakfastStatus {
  is_open: boolean;
  order_date: string | null;
  target_date: string | null;
  cutoff_time: string;
  reopen_time: string;
  current_time: string;
  message: string;
}

export interface KitchenStatus {
  default_view: 'breakfast' | 'lunch';
  current_time: string;
  next_switch: string;
}

export interface OrderingStatus {
  lunch: LunchStatus;
  breakfast: BreakfastStatus;
  kitchen: KitchenStatus;
}

/**
 * Fetch complete ordering status from the backend.
 * Includes lunch, breakfast, and kitchen view status.
 */
async function fetchOrderingStatus(): Promise<OrderingStatus> {
  const response = await fetch(`${apiConfig.baseUrl}/orders/status`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch ordering status');
  }
  
  return response.json();
}

/**
 * Hook to get the complete ordering status.
 * Auto-refreshes every 60 seconds to keep status current.
 */
export function useOrderingStatus() {
  return useQuery({
    queryKey: ['ordering-status'],
    queryFn: fetchOrderingStatus,
    staleTime: 30 * 1000, // Consider stale after 30 seconds
    refetchInterval: 60 * 1000, // Refetch every 60 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to get just the kitchen status.
 * Useful for the kitchen prep view to determine default tab.
 */
export function useKitchenStatus() {
  const { data, isLoading, error } = useOrderingStatus();
  
  return {
    kitchenStatus: data?.kitchen,
    isLoading,
    error,
  };
}

/**
 * Hook to get lunch ordering status.
 */
export function useLunchStatus() {
  const { data, isLoading, error } = useOrderingStatus();
  
  return {
    lunchStatus: data?.lunch,
    isLoading,
    error,
  };
}

/**
 * Hook to get breakfast ordering status.
 */
export function useBreakfastStatus() {
  const { data, isLoading, error } = useOrderingStatus();
  
  return {
    breakfastStatus: data?.breakfast,
    isLoading,
    error,
  };
}

export default useOrderingStatus;
